import { useEffect, useRef, useState, type DragEvent } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, Typography,
} from "@mui/material";
import CloudUpload from "@mui/icons-material/CloudUpload";
import type { BillingOverview, PaidPlanId } from "@ebook/contracts";
import { api } from "../lib/api-client";
import { uploadPaymentSlip } from "../lib/asset-transfer-client";
import { plans } from "../lib/plans";
import { GlobalFileInput, GlobalInput } from "./GlobalInput";
import { PageLoading } from "./PageState";
import { useTranslate } from "../i18n";

const paidPlans = plans.filter((plan): plan is typeof plan & { id: PaidPlanId } => plan.id !== "free");
const allowedTypes = new Set(["image/png", "image/jpeg", "image/webp", "application/pdf"]);

type Props = {
  open: boolean;
  initialPlan: PaidPlanId;
  onClose: () => void;
  onActivated: () => void;
};

export function PaymentSlipDialog({ open, initialPlan, onClose, onActivated }: Props) {
  const __ = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [billing, setBilling] = useState<BillingOverview | null>(null);
  const [plan, setPlan] = useState<PaidPlanId>(initialPlan);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setPlan(initialPlan); setFile(null); setError(""); setBilling(null);
    void api.billing().then(setBilling).catch((caught) => setError(caught instanceof Error ? caught.message : __("โหลดข้อมูลชำระเงินไม่สำเร็จ")));
  }, [__, initialPlan, open]);

  function chooseFile(next?: File) {
    if (!next) return;
    const inferred = next.type || (next.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "");
    if (!allowedTypes.has(inferred)) { setError(__("รองรับ PNG, JPEG, WebP หรือ PDF เท่านั้น")); return; }
    if (next.size <= 0 || next.size > 5 * 1024 * 1024) { setError(__("สลิปต้องมีขนาดไม่เกิน 5 MiB")); return; }
    setFile(next); setError("");
  }

  function drop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault(); setDragging(false); chooseFile(event.dataTransfer.files[0]);
  }

  async function submit() {
    if (!file || busy) return;
    setBusy(true); setError("");
    try {
      const contentType = file.type || (file.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "image/jpeg");
      const started = await api.startPayment({ plan, fileName: file.name, contentType, size: file.size });
      await uploadPaymentSlip(file, started);
      await api.completePayment(started.request.id, started.objectKey);
      onActivated();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("ส่งสลิปไม่สำเร็จ"));
      void api.billing().then(setBilling).catch(() => undefined);
    } finally { setBusy(false); }
  }

  const openRequest = billing?.currentRequest && ["uploading", "pending"].includes(billing.currentRequest.status)
    ? billing.currentRequest : null;
  return <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle fontWeight={800}>{__("อัปเกรดแผนด้วยสลิปการชำระเงิน")}</DialogTitle>
    <DialogContent>
      {!billing && !error ? <PageLoading /> : <Stack spacing={2.25}>
        {error && <Alert severity="error">{error}</Alert>}
        {billing?.currentRequest?.status === "rejected" && <Alert severity="error">
          {__("สลิปล่าสุดถูกปฏิเสธ: {{reason}}", { reason: billing.currentRequest.rejectionReason || __("ไม่ได้ระบุเหตุผล") })}
        </Alert>}
        {openRequest ? <Alert severity={openRequest.status === "pending" ? "info" : "warning"}>
          {openRequest.status === "pending"
            ? __("สลิปกำลังรอตรวจสอบ คุณใช้แผน {{plan}} แบบทดลองได้ถึง {{date}}", { plan: openRequest.requestedPlan, date: openRequest.trialExpiresAt ? new Date(openRequest.trialExpiresAt).toLocaleDateString("th-TH") : "-" })
            : __("มีคำขอที่ยังอัปโหลดไม่เสร็จ กรุณาเลือกแผน Free เพื่อยกเลิกคำขอนี้ก่อนเริ่มใหม่")}
        </Alert> : <>
          <Box>
            <Typography variant="body2" fontWeight={700} color="text.secondary" mb={0.75}>
              {__("แผนที่ต้องการ")}
            </Typography>
            <GlobalInput
              select
              fullWidth
              value={plan}
              onChange={(event) => setPlan(event.target.value as PaidPlanId)}
            >
              {paidPlans.map((item) => <MenuItem key={item.id} value={item.id}>{item.name} — {item.priceLabel}</MenuItem>)}
            </GlobalInput>
          </Box>
          <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
            <Typography fontWeight={800}>{billing?.config.bankName || __("บัญชีธนาคาร")}</Typography>
            <Typography>{billing?.config.accountName || "-"}</Typography>
            <Typography variant="h6" fontWeight={800}>{billing?.config.accountNumber || "-"}</Typography>
          </Box>
          <Alert severity="info">{__("หลังส่งสลิปโปรดรอระหว่างตรวจสอบ")}</Alert>
          <Box onDragEnter={(event) => { event.preventDefault(); setDragging(true); }} onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)} onDrop={drop}
            onClick={() => inputRef.current?.click()}
            sx={{ p: 3, minHeight: 150, border: "2px dashed", borderColor: dragging ? "primary.main" : "divider", borderRadius: 2, display: "grid", placeItems: "center", textAlign: "center", cursor: "pointer", bgcolor: dragging ? "action.hover" : "transparent" }}>
            <Stack alignItems="center" spacing={1}><CloudUpload color="primary" />
              <Typography fontWeight={700}>{file?.name ?? __("ลากสลิปมาวาง หรือคลิกเพื่อเลือกไฟล์")}</Typography>
              <Typography variant="caption" color="text.secondary">PNG, JPEG, WebP, PDF · สูงสุด 5 MiB</Typography>
            </Stack>
            <GlobalFileInput ref={inputRef} accept="image/png,image/jpeg,image/webp,application/pdf,.pdf" onChange={(event) => { chooseFile(event.target.files?.[0]); event.target.value = ""; }} />
          </Box>
        </>}
      </Stack>}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3 }}><Button onClick={onClose} disabled={busy}>{__("ปิด")}</Button>
      {!openRequest && <Button variant="contained" disabled={!file || busy || !billing} onClick={() => void submit()}>{busy ? __("กำลังส่ง…") : __("ส่งสลิปและเริ่มทดลองใช้")}</Button>}
    </DialogActions>
  </Dialog>;
}
